/*
 * mfb-bundler-group.js
 *
 * Groups multiple Bundler.app widgets on a single PDP into a tabs or
 * accordion UI. Does not touch Bundler's internal DOM — only adds sibling
 * nav/trigger elements and toggles a visibility class on each [data-bundle]
 * wrapper.
 *
 * Mode: read from the closest [data-mfb-bundle-mode] ancestor on the first
 * [data-bundle], or defaults to 'tabs'. Values: 'tabs' | 'accordion' | 'stacked'.
 */
(function () {
  'use strict';

  const BUNDLE_SELECTOR = '[data-bundle]';
  const SETTLE_MS = 400;

  let settleTimer = null;
  let currentGroup = null;

  function readMode(firstBundle) {
    const scope = firstBundle.closest('[data-mfb-bundle-mode]');
    const mode = scope && scope.getAttribute('data-mfb-bundle-mode');
    return mode || 'tabs';
  }

  function tearDown() {
    document.querySelectorAll('.mfb-bundler-group-nav, .mfb-bundler-accordion-trigger').forEach(el => el.remove());
    document.querySelectorAll(BUNDLE_SELECTOR).forEach(el => {
      el.classList.remove('mfb-bundle-item', 'is-active', 'is-hidden', 'is-collapsed');
      el.removeAttribute('role');
      el.removeAttribute('aria-labelledby');
      el.style.removeProperty('display');
    });
  }

  function buildTabs(bundles, parent) {
    const nav = document.createElement('div');
    nav.className = 'mfb-bundler-group-nav mfb-bundler-group-nav--tabs';
    nav.setAttribute('role', 'tablist');
    nav.setAttribute('aria-label', 'Bundle options');

    bundles.forEach((bundle, i) => {
      const titleEl = bundle.querySelector('.bndlr-bundle-title');
      const label = titleEl ? titleEl.textContent.trim() : `Bundle ${i + 1}`;
      const tabId = `mfb-bundle-tab-${i}`;
      const panelId = `mfb-bundle-panel-${i}`;

      const tab = document.createElement('button');
      tab.type = 'button';
      tab.className = 'mfb-bundler-tab';
      tab.id = tabId;
      tab.setAttribute('role', 'tab');
      tab.setAttribute('aria-controls', panelId);
      tab.setAttribute('aria-selected', i === 0 ? 'true' : 'false');
      tab.textContent = label;
      if (i === 0) tab.classList.add('is-active');
      nav.appendChild(tab);

      bundle.id = panelId;
      bundle.setAttribute('role', 'tabpanel');
      bundle.setAttribute('aria-labelledby', tabId);
      bundle.classList.add('mfb-bundle-item');
      bundle.classList.toggle('is-active', i === 0);
      bundle.classList.toggle('is-hidden', i !== 0);
    });

    parent.insertBefore(nav, bundles[0]);

    nav.addEventListener('click', e => {
      const btn = e.target.closest('.mfb-bundler-tab');
      if (!btn) return;
      const panelId = btn.getAttribute('aria-controls');
      nav.querySelectorAll('.mfb-bundler-tab').forEach(t => {
        const match = t === btn;
        t.classList.toggle('is-active', match);
        t.setAttribute('aria-selected', match ? 'true' : 'false');
      });
      bundles.forEach(bundle => {
        const match = bundle.id === panelId;
        bundle.classList.toggle('is-active', match);
        bundle.classList.toggle('is-hidden', !match);
      });
    });
  }

  function buildAccordion(bundles, parent) {
    bundles.forEach((bundle, i) => {
      const titleEl = bundle.querySelector('.bndlr-bundle-title');
      const label = titleEl ? titleEl.textContent.trim() : `Bundle ${i + 1}`;
      const triggerId = `mfb-bundle-trigger-${i}`;
      const panelId = `mfb-bundle-panel-${i}`;

      bundle.id = panelId;
      bundle.classList.add('mfb-bundle-item');
      bundle.classList.toggle('is-collapsed', i !== 0);
      bundle.setAttribute('role', 'region');
      bundle.setAttribute('aria-labelledby', triggerId);

      const trigger = document.createElement('button');
      trigger.type = 'button';
      trigger.className = 'mfb-bundler-accordion-trigger';
      trigger.id = triggerId;
      trigger.setAttribute('aria-controls', panelId);
      trigger.setAttribute('aria-expanded', i === 0 ? 'true' : 'false');
      trigger.innerHTML = `<span class="mfb-bundler-accordion-label">${label}</span><span class="mfb-bundler-accordion-icon" aria-hidden="true"></span>`;

      parent.insertBefore(trigger, bundle);

      trigger.addEventListener('click', () => {
        const willOpen = trigger.getAttribute('aria-expanded') !== 'true';
        trigger.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
        bundle.classList.toggle('is-collapsed', !willOpen);
      });
    });
  }

  function groupBundles() {
    const bundles = Array.from(document.querySelectorAll(BUNDLE_SELECTOR));
    if (bundles.length < 2) return;

    const parent = bundles[0].parentElement;
    const allSameParent = bundles.every(b => b.parentElement === parent);
    if (!parent || !allSameParent) {
      return;
    }

    const mode = readMode(bundles[0]);
    parent.setAttribute('data-mfb-bundle-mode-resolved', mode);

    if (mode === 'stacked') {
      return;
    }

    if (mode === 'accordion') {
      buildAccordion(bundles, parent);
    } else {
      buildTabs(bundles, parent);
    }

    currentGroup = { parent, bundles };
  }

  function reinit() {
    tearDown();
    currentGroup = null;
    groupBundles();
  }

  function scheduleGroup() {
    if (settleTimer) clearTimeout(settleTimer);
    settleTimer = setTimeout(() => {
      if (!currentGroup) groupBundles();
    }, SETTLE_MS);
  }

  function init() {
    scheduleGroup();

    const observer = new MutationObserver(muts => {
      for (const m of muts) {
        for (const node of m.addedNodes) {
          if (node.nodeType === 1 && (node.matches && node.matches(BUNDLE_SELECTOR) || node.querySelector && node.querySelector(BUNDLE_SELECTOR))) {
            scheduleGroup();
            return;
          }
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  window.MFBBundlerGroup = { reinit, tearDown };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
